import React, { Children } from 'react';
import { Check, X, AlertCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
const scrollToFinalCta = (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById('final-cta')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ContentSections() {
  return <div className="w-full" data-id="element-1">
      {/* 1. La Promesa (Light) */}
      <section className="py-20 px-4 bg-[#f5f0e8] text-[#0f1b2d]" data-id="element-2">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-3xl mx-auto text-center" data-id="element-3">
          <h2 className="text-3xl md:text-4xl font-black mb-8" data-id="element-4">
            La promesa — sin fantasía
          </h2>
          <div className="text-lg md:text-xl leading-relaxed space-y-6 text-[#0f1b2d]/80" data-id="element-5">
            <p data-id="element-6">
              En <strong className="text-[#0f1b2d]" data-id="element-7">14 días</strong> vas a tener
              un{' '}
              <strong className="text-[#0f1b2d]" data-id="element-8">
                micro-servicio vendible
              </strong>{' '}
              que puedas ofrecer por WhatsApp para generar{' '}
              <strong className="text-[#0f1b2d]" data-id="element-9">100€ extra a la semana</strong>
              .
            </p>
            <p data-id="element-10">No te voy a decir que te hagas rico.</p>
            <div className="bg-[#25D366]/10 border-l-4 border-[#25D366] p-6 rounded-r-lg text-left md:text-center mt-8" data-id="element-11">
              <p className="text-xl md:text-2xl font-bold text-[#0f1b2d]" data-id="element-12">
                "Te voy a enseñar a cobrar por algo pequeño, concreto y
                repetible."
              </p>
            </div>

            {/* Money image - reinforces the tangible income message */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="mt-10 rounded-2xl overflow-hidden shadow-xl" data-id="element-13">
              <img src="https://images.unsplash.com/photo-1554768804-50c1e2b50a6e?w=800&q=80&auto=format&fit=crop" alt="Cartera con billetes de euro representando ingresos extra" className="w-full h-56 md:h-72 object-cover" data-id="element-14" />
            </motion.div>

            {/* CTA after promise */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex flex-col items-center gap-2 pt-4" data-id="element-15">
              <a href="#final-cta" onClick={scrollToFinalCta} className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-[#0f1b2d] font-bold text-lg py-4 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" data-id="element-16">
                Quiero empezar hoy
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-17" />
              </a>
              <span className="text-[#0f1b2d]/50 text-sm font-medium flex items-center gap-2" data-id="element-18">
                <span className="line-through opacity-60" data-id="element-19">29€</span>
                <span className="bg-red-500/90 text-white text-xs font-bold px-2 py-0.5 rounded" data-id="element-20">
                  -78%
                </span>
                <span className="text-[#25D366] font-bold" data-id="element-21">6,36€</span>
              </span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 2. Lo que esto NO es (Dark) */}
      <section className="py-20 px-4 bg-[#0f1b2d] text-[#f5f0e8]" data-id="element-22">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="max-w-4xl mx-auto" data-id="element-23">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl font-black text-center mb-12" data-id="element-24">
            Lo que esto NO es{' '}
            <span className="text-[#f5f0e8]/50 text-xl block md:inline font-normal md:ml-2" data-id="element-25">
              (para evitar la decepción)
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-26">
            {/* NO ES */}
            <motion.div variants={fadeIn} className="bg-[#f5f0e8]/5 rounded-2xl p-8 border border-red-500/20" data-id="element-27">
              <h3 className="text-xl font-bold text-red-400 mb-6 flex items-center gap-2" data-id="element-28">
                <X className="w-6 h-6" data-id="element-29" /> Esto NO es:
              </h3>
              <ul className="space-y-4" data-id="element-30">
                {['"ingresos pasivos"', 'un curso largo', 'una marca personal', 'un proyecto que te pida otra vida'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#f5f0e8]/80" data-id="element-31">
                    <X className="w-5 h-5 text-red-400/50 mt-0.5 shrink-0" data-id="element-32" />
                    <span data-id="element-33">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* SI ES */}
            <motion.div variants={fadeIn} className="bg-[#25D366]/5 rounded-2xl p-8 border border-[#25D366]/20" data-id="element-34">
              <h3 className="text-xl font-bold text-[#25D366] mb-6 flex items-center gap-2" data-id="element-35">
                <Check className="w-6 h-6" data-id="element-36" /> Esto SÍ es:
              </h3>
              <ul className="space-y-4" data-id="element-37">
                {['una oferta simple', 'mensajes listos', 'entrega en 60–90 minutos', 'repetición semanal'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#f5f0e8]/80" data-id="element-38">
                    <Check className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" data-id="element-39" />
                    <span className="font-medium text-[#f5f0e8]" data-id="element-40">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 3. Para quién es (Light) */}
      <section className="py-20 px-4 bg-[#f5f0e8] text-[#0f1b2d]" data-id="element-41">
        <div className="max-w-4xl mx-auto" data-id="element-42">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-black text-center mb-12" data-id="element-43">
            Para quién es (y para quién no)
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-44">
            {/* ES PARA TI */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl p-8 shadow-xl shadow-[#25D366]/5 border-t-4 border-[#25D366]" data-id="element-45">
              <h3 className="text-xl font-bold mb-6 text-[#0f1b2d]" data-id="element-46">
                Es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-47">
                {['tienes trabajo y necesitas un extra', 'puedes sacar 5 horas/semana', 'tienes habilidades normales de oficina (o ganas de hacer cosas simples)', 'prefieres empezar con tu entorno antes que "exponerte"'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-48">
                    <div className="bg-[#25D366]/10 p-1 rounded-full mt-0.5 shrink-0" data-id="element-49">
                      <Check className="w-3 h-3 text-[#25D366]" data-id="element-50" />
                    </div>
                    <span className="text-gray-700" data-id="element-51">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* NO ES PARA TI */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-2xl p-8 shadow-xl shadow-red-500/5 border-t-4 border-red-400" data-id="element-52">
              <h3 className="text-xl font-bold mb-6 text-[#0f1b2d]" data-id="element-53">
                No es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-54">
                {['no quieres hablar con nadie', 'quieres resultados sin enviar mensajes', 'buscas "el negocio de tu vida" (esto es un sistema pequeño, no una identidad)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-55">
                    <div className="bg-red-100 p-1 rounded-full mt-0.5 shrink-0" data-id="element-56">
                      <X className="w-3 h-3 text-red-500" data-id="element-57" />
                    </div>
                    <span className="text-gray-700" data-id="element-58">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. El problema real (Dark) */}
      <section className="py-24 px-4 bg-[#0f1b2d] text-[#f5f0e8]" data-id="element-59">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="max-w-3xl mx-auto text-center" data-id="element-60">
          <h2 className="text-3xl md:text-5xl font-black mb-8 leading-tight" data-id="element-61">
            El problema real <br data-id="element-62" />
            <span className="text-[#f5f0e8]/50 text-2xl md:text-3xl font-normal" data-id="element-63">
              (el que te come por dentro)
            </span>
          </h2>

          {/* Moody money image */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mb-10 rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-[#f5f0e8]/5" data-id="element-64">
            <img src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80&auto=format&fit=crop" alt="Monedas y billetes representando la presión económica del día a día" className="w-full h-48 md:h-64 object-cover opacity-80" data-id="element-65" />
          </motion.div>

          <div className="text-xl md:text-2xl leading-relaxed space-y-8 text-[#f5f0e8]/90 font-medium" data-id="element-66">
            <p data-id="element-67">No es solo el dinero.</p>
            <p data-id="element-68">Es la sensación de que vas cumpliendo… y aun así vas detrás.</p>
            <p className="text-[#f5f0e8]/70" data-id="element-69">
              Que el mes no se rompe por un capricho, sino por la suma de cosas
              normales.
            </p>
            <div className="py-4" data-id="element-70">
              <p className="text-[#25D366] font-bold text-2xl md:text-3xl" data-id="element-71">
                Y encima, cuando piensas en "hacer algo", tu cuerpo pide sofá.
              </p>
            </div>
            <p data-id="element-72">No por vago. Por agotado.</p>
          </div>

          {/* CTA after problem section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex flex-col items-center gap-2 mt-12" data-id="element-73">
            <a href="#final-cta" onClick={scrollToFinalCta} className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-[#0f1b2d] font-bold text-lg py-4 px-10 rounded-xl shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:shadow-[0_0_50px_rgba(37,211,102,0.5)] transition-all duration-300 transform hover:scale-105" data-id="element-74">
              Ver la oferta
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-75" />
            </a>
            <span className="text-[#f5f0e8]/40 text-sm font-medium flex items-center gap-2" data-id="element-76">
              <span className="line-through opacity-60" data-id="element-77">29€</span>
              <span className="bg-red-500/90 text-white text-xs font-bold px-2 py-0.5 rounded" data-id="element-78">
                -78%
              </span>
              <span className="text-[#25D366] font-bold" data-id="element-79">6,36€</span>
            </span>
          </motion.div>
        </motion.div>
      </section>

      {/* 5. Por qué falla lo demás (Light) */}
      <section className="py-20 px-4 bg-[#f5f0e8] text-[#0f1b2d]" data-id="element-80">
        <div className="max-w-4xl mx-auto" data-id="element-81">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-black text-center mb-12" data-id="element-82">
            Por qué lo que has probado falla
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-83">
            {[{
            title: 'Listas de ideas',
            desc: 'Te dejan igual, porque no eliges ninguna.'
          }, {
            title: 'Ingresos pasivos',
            desc: 'Lento y normalmente requiere contenido constante.'
          }, {
            title: 'Cursos enormes',
            desc: 'Te convierten en estudiante otra vez.'
          }, {
            title: '"Emprender"',
            desc: 'Demasiadas piezas, demasiada presión mental.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-84">
                <div className="flex items-start gap-4" data-id="element-85">
                  <AlertCircle className="w-6 h-6 text-orange-400 shrink-0" data-id="element-86" />
                  <div data-id="element-87">
                    <h4 className="font-bold text-lg mb-1" data-id="element-88">{item.title}</h4>
                    <p className="text-gray-600" data-id="element-89">{item.desc}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-[#0f1b2d] text-[#f5f0e8] p-8 md:p-10 rounded-2xl text-center shadow-2xl" data-id="element-90">
            <p className="text-xl md:text-2xl font-medium" data-id="element-91">
              Tu problema no es información.
              <br data-id="element-92" />
              <span className="text-[#25D366] font-bold mt-2 block text-2xl md:text-3xl" data-id="element-93">
                Tu problema es decisión + ejecución con poco tiempo.
              </span>
            </p>
          </motion.div>

          {/* CTA after "why everything else fails" */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex flex-col items-center gap-2 mt-12" data-id="element-94">
            <a href="#final-cta" onClick={scrollToFinalCta} className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-[#0f1b2d] font-bold text-lg py-4 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" data-id="element-95">
              Esto es lo que necesito
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-96" />
            </a>
            <span className="text-[#0f1b2d]/50 text-sm font-medium flex items-center gap-2" data-id="element-97">
              <span className="line-through opacity-60" data-id="element-98">29€</span>
              <span className="bg-red-500/90 text-white text-xs font-bold px-2 py-0.5 rounded" data-id="element-99">
                -78%
              </span>
              <span className="text-[#25D366] font-bold" data-id="element-100">6,36€</span>
            </span>
          </motion.div>
        </div>
      </section>
    </div>;
}